import React from "react";
import { Link } from "react-router-dom";
import { getRelativeTime } from "../../common/DateHelpers";
import CreditUsageIndicator from "../creditNotes/components/CreditUsageIndicator";
import {
  fmtHoverDateTime,
  fmtOrderRupee,
  formatDealerOrderStatusDisplay,
} from "../pages/storeSalesDashboardHelpers";

export default function DealerOrderCard({
  row,
  onBuyerClick,
  onReviewOrder,
  onOpenShipmentInvoice,
  acceptingProjectId = null,
  embeddedInOrderSummary = false,
}) {
  const st = String(row?.status || "").trim();
  const statusDetailRaw = row?.status_detail_at ?? null;
  const hasStatusDetail =
    typeof statusDetailRaw === "string" && statusDetailRaw.trim().length > 0;
  const statusDetailLine = hasStatusDetail
    ? getRelativeTime(statusDetailRaw)
    : "—";
  const statusDisplay = formatDealerOrderStatusDisplay(st);
  const pname = String(row?.name || "").trim() || "Untitled project";
  const pid = row?.id ?? "";
  const buyerId = row?.buyer_id;
  const buyerLabel = String(row?.buyer_name || "").trim() || "-";
  const rowMode = String(row?.mode || "").trim().toLowerCase();
  const canDealerAccept = st === "placed" && rowMode !== "direct";
  const shipments = Array.isArray(row?.shipments) ? row.shipments : [];
  return (
    <div
      className={
        embeddedInOrderSummary
          ? "mt-3 pt-3 mb-2 border-top"
          : "border rounded-3 bg-white shadow-sm p-3"
      }
    >
      <div
        className={`d-flex flex-wrap align-items-start justify-content-between gap-2 ${
          embeddedInOrderSummary ? "mb-3" : "mb-1"
        }`}
      >
        <div className="min-w-0">
          {buyerId != null && typeof onBuyerClick === "function" ? (
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none text-body fw-semibold fs-6"
              onClick={() => onBuyerClick(buyerId)}
            >
              {buyerLabel}
            </button>
          ) : (
            <span className="fw-semibold fs-6">{buyerLabel}</span>
          )}
        </div>
        <div className="text-end text-nowrap">
          <div className="fw-semibold text-body fs-6">
            {fmtOrderRupee(row?.value)}
          </div>
          <button
            type="button"
            className="btn btn-link p-0 small text-decoration-none text-muted"
            onClick={() => onReviewOrder?.(row, { showAcceptButton: false })}
          >
            Payable{" "}
            <span className="fw-semibold text-body">
              {fmtOrderRupee(row?.payable_total ?? row?.value)}
            </span>
          </button>
        </div>
      </div>

      {!embeddedInOrderSummary ? (
        <div className="mb-2">
          <Link
            to={`/projects/${pid}`}
            className="text-decoration-none small text-muted"
          >
            {pname}
          </Link>
        </div>
      ) : null}

      <div
        className="small mb-1"
        title={
          hasStatusDetail ? fmtHoverDateTime(statusDetailRaw) : undefined
        }
      >
        <span className="fw-semibold text-body">{statusDisplay.primary}</span>
        {statusDisplay.secondary ? (
          <div className="text-muted">{statusDisplay.secondary}</div>
        ) : null}
        {!statusDisplay.secondary ? (
          <span className="text-muted"> ({statusDetailLine})</span>
        ) : null}
      </div>

      <div className="small mb-2">
        <CreditUsageIndicator
          credit_adjustments_total={row?.credit_adjustments_total}
          credit_note_identifiers={row?.credit_note_identifiers}
          compact
        />
      </div>

      {canDealerAccept ? (
        <div className="mb-3">
          <button
            type="button"
            className="btn btn-sm btn-success"
            disabled={acceptingProjectId != null}
            onClick={() => onReviewOrder?.(row, { showAcceptButton: true })}
          >
            Review to accept order
          </button>
        </div>
      ) : null}

      {!embeddedInOrderSummary ? (
        shipments.length === 0 ? (
          <div className="small text-muted">No shipments.</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {shipments.map((shipment) => (
              <div
                key={shipment.id}
                className="d-flex align-items-center justify-content-between gap-2 border rounded-2 px-2 py-1 bg-light"
              >
                <div className="d-flex align-items-center gap-3">
                  <span className="small fw-semibold">{shipment.name}</span>
                  <button
                    type="button"
                    className="btn fs-6 btn-link p-0"
                    title="View invoice"
                    aria-label="View invoice"
                    onClick={() => onOpenShipmentInvoice?.(shipment)}
                  >
                    <i
                      className="fa-solid fa-circle-info"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <span className="small text-capitalize">{shipment.status}</span>
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}
